<template>
  <div>
    <Loader v-if="isFetching" />
    <template v-else>
      <div class="row obsform public-observation">
        <div class="col-12 typography">
          <div class="container mt-2">
            <div class="row mb-4 hide-print">
              <div class="col-12">
                <a href="#" @click.prevent="goBack"><i class="fa fa-angle-left mr-2"></i>Back</a>
                <social-sharing
                  :url="socialSharing.url"
                  :title="socialSharing.title"
                  :description="socialSharing.description"
                  inline-template
                >
                  <div class="social-share">
                    Share:
                    <network network="facebook">
                      <img alt="Facebook" src="/resources/themes/default/images/facebook.svg" />
                      <span class="sr-only">Facebook</span>
                    </network>
                  </div>
                </social-sharing>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12 col-lg-6 col-print-6">
                <div class="row">
                  <div class="col-7 col-lg-12"><h1 class="observation-header">Public Observation</h1></div>

                  <div class="col-5 d-lg-none text-right">
                    <button
                      type="button"
                      class="map-control public-observation__map-btn"
                      @click.prevent="handleMobileToggle"
                    >
                      Show Map
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="selectedObservation.coordinates" class="col-12 col-lg-6 col-print-6 text-right">
                <p class="text-small mb-1">Pin drop coordinates</p>
                <p>
                  <strong>Lat:</strong> {{ selectedObservation.coordinates.lat }} <strong class="ml-2">Lng:</strong>
                  {{ selectedObservation.coordinates.lng }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 pr-0 pl-0 hide-print flex-first">
          <Mapbox
            :styles="{ 'map-wrapper--strip': true, 'map-wrapper--active': mobileMapVisible }"
            :hidden="false"
            :expanded="true"
            :options="mapOptions"
            :regions="regions"
            :forecasts="forecasts"
            :observations="observations"
            :onLoad="handleMapLoad"
            :onRegionSelect="handleRegionSelect"
            :onObservationSelect="handleObservationSelect"
            :onMobileToggle="handleMobileToggle"
          />
        </div>

        <div class="col-12">
          <div class="container">
            <div class="row">
              <div class="col-12">
                <p class="mb-2">
                  Observation by
                  {{ selectedObservation.observer.name ? selectedObservation.observer.name : "Anonymous" }} at
                  <strong>{{ selectedObservation.time | niceDate }}</strong>
                </p>
                <p class="text-small">Public observations will appear for 7 days from submitted date</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row background-lightest-grey pt-4 pb-2 mb-4 mt-4">
        <div class="col-12">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-6 col-print-6">
                <p>
                  <strong>Observation Type:</strong><br />
                  {{ selectedObservation.type }}
                </p>
                <p>
                  <strong>Description:</strong><br />
                  {{ selectedObservation.description }}
                </p>
              </div>
              <div v-if="isAvalanche" class="col-12 col-lg-6 col-print-6">
                <p>
                  <strong>People Involved:</strong><br />
                  {{ selectedObservation.peopleInvolved | yesNo }}
                </p>
                <p>
                  <strong>Structures Involved:</strong><br />
                  {{ selectedObservation.structuresInvolved | yesNo }}
                </p>
              </div>
              <div v-if="isSnowpack" class="col-12 col-lg-6 col-print-6">
                <p>
                  <strong>Surface Conditions:</strong><br />
                  {{ selectedObservation.surfaceConditions | capitalise }}
                </p>
                <p>
                  <strong>Fracture Character:</strong><br />
                  {{ selectedObservation.fractureCharacter | capitalise }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row typography">
        <div class="col-12">
          <div class="container">
            <div class="row mb-2 mt-4">
              <div v-if="isAvalanche && selectedObservation.triggerType" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Trigger:</strong><br />
                {{ selectedObservation.triggerType | capitalise }}
              </div>
              <div v-if="isAvalanche && selectedObservation.triggerTime" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Trigger Time:</strong><br />
                {{ selectedObservation.triggerTime | niceDate }}
              </div>
              <div
                v-if="isAvalanche && selectedObservation.triggerTimeEstimated"
                class="col-12 col-lg-6 col-print-6 mb-4"
              >
                <strong>Estimated Trigger Time:</strong><br />
                {{ selectedObservation.triggerTimeEstimated }} hours before observation
              </div>
              <div v-if="isAvalanche && selectedObservation.length" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Length:</strong><br />
                {{ selectedObservation.length }} metres
              </div>
              <div v-if="isAvalanche && selectedObservation.width" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Width:</strong><br />
                {{ selectedObservation.width }} metres
              </div>
              <div v-if="selectedObservation.depth" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong v-if="isAvalanche">Depth:</strong><strong v-if="isSnowpack">Snowpack depth:</strong><br />
                {{ selectedObservation.depth }} cm
              </div>
              <div v-if="isAvalanche && selectedObservation.size" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Size:</strong><br />
                {{ selectedObservation.size }}
              </div>
              <div v-if="isAvalanche && selectedObservation.character" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Character:</strong><br />
                {{ selectedObservation.character }}
              </div>
              <div v-if="selectedObservation.aspect" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Aspect:</strong><br />
                {{ selectedObservation.aspect }}
              </div>
              <div v-if="selectedObservation.elevation" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Elevation:</strong><br />
                {{ selectedObservation.elevation }} metres
              </div>
              <div v-if="isAvalanche && selectedObservation.slopeAngle" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Slope Angle:</strong><br />
                {{ selectedObservation.slopeAngle }} degrees
              </div>
              <div v-if="isSnowpack && selectedObservation.surfaceConditions" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Surface Conditions:</strong><br />
                {{ selectedObservation.surfaceConditions | capitalise }}
              </div>
              <div
                v-if="isSnowpack && (selectedObservation.footPenetration || selectedObservation.skiPenetration)"
                class="col-12 col-lg-6 col-print-6 mb-4"
              >
                <strong>Test type:</strong><br />
                {{ snowpackTestType }} penetration
              </div>
              <div
                v-if="isSnowpack && (selectedObservation.footPenetration || selectedObservation.skiPenetration)"
                class="col-12 col-lg-6 col-print-6 mb-4"
              >
                <strong>Penetration depth:</strong><br />
                <span v-if="selectedObservation.footPenetration">
                  {{ selectedObservation.footPenetration }}
                </span>
                <span v-if="selectedObservation.skiPenetration">
                  {{ selectedObservation.skiPenetration }}
                </span>
                cm
              </div>
              <div v-if="isSnowpack && selectedObservation.testResult" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Test result:</strong><br />
                {{ selectedObservation.testResult | capitalise }}
              </div>
              <div v-if="isSnowpack && selectedObservation.fractureCharacter" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Test Fracture Character:</strong><br />
                {{ selectedObservation.fractureCharacter | capitalise }}
              </div>
              <div v-if="isSnowpack && selectedObservation.fractureDepth" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Fracture Depth:</strong><br />
                {{ selectedObservation.fractureDepth }}
              </div>
              <div v-if="selectedObservation.comments" class="col-12 mb-4">
                <strong>Comments:</strong><br />
                {{ selectedObservation.comments }}
              </div>
            </div>

            <div v-if="selectedObservation.peopleInvolved" class="row mt-4">
              <div class="col-12">
                <h2>People involved</h2>
              </div>
              <div v-if="selectedObservation.peopleInvolved.activityType" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Activity Type:</strong><br />
                {{ selectedObservation.peopleInvolved.activityType | capitalise }}
              </div>
              <div v-if="selectedObservation.peopleInvolved.groupSize" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>Group Size:</strong><br />
                {{ selectedObservation.peopleInvolved.groupSize }}
              </div>
              <div v-if="selectedObservation.peopleInvolved.peopleCaught" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>People Caught:</strong><br />
                {{ selectedObservation.peopleInvolved.peopleCaught }}
              </div>
              <div v-if="selectedObservation.peopleInvolved.peopleInjured" class="col-12 col-lg-6 col-print-6 mb-4">
                <strong>People Injured:</strong><br />
                {{ selectedObservation.peopleInvolved.peopleInjured }}
              </div>
            </div>

            <div v-if="selectedObservation.structuresInvolved" class="row mt-4">
              <div class="col-12">
                <h2>Structures involved</h2>
              </div>
              <div class="col-12 mb-4">
                <strong>Description:</strong><br />
                {{ selectedObservation.structuresInvolved }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row typography">
        <div class="col-12">
          <div class="container">
            <div class="row mb-2 publicobs__photos">
              <vue-easy-lightbox
                :visible="visible"
                :imgs="photos"
                :index="index"
                @hide="handleHide"
              ></vue-easy-lightbox>

              <div class="col-6" v-for="(photo, index) in selectedObservation.photos">
                <a :href="photo.full" @click.prevent="() => showImg(index)" :key="index">
                  <img :src="photo.thumb" alt="" width="100%" class="rounded mr-4 mb-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="container pt-4 pb-4 mt-4 mb-5 hide-print">
            <span class="h5 font-weight-bold">Search historic public observations</span>
            <i class="fa fa-lg fa-angle-right mx-2"></i>
            <a
              href="/observations"
              class="button button--dark-with-light-outline d-inline-block"
              @click.prevent="$router.push({ name: 'observations' })"
            >
              <PublicMarker :styles="{ 'd-inline-block': true, 'align-middle': true }" />
              View now
            </a>
          </div>
          <div class="container border-top-light-grey border-bottom-light-grey pt-4 pb-4 mt-4 mb-5 hide-print">
            <div class="row h-100 align-items-center">
              <div class="col-12 col-lg-8">
                <p class="footer__text footer__text--contained mb-0">
                  <strong class="footer__text--large">{{ cms.publicObsCallToActionTitle }}</strong>
                  <span>{{ cms.publicObsCallToActionDescription }}</span>
                </p>
              </div>
              <div class="col-12 col-lg-4 text-right">
                <Button
                  :styles="{
                    'pl-2': true,
                    'pr-3': true,
                    'button--public-obs': true,
                    'text-truncate': true,
                    'mt-2': true,
                    'mt-md-0': true
                  }"
                  :onClick="handleReportObservationClick"
                >
                  <span slot="label">
                    <PublicMarker :styles="{ 'd-inline-block': true, 'align-middle': true }" />
                    <span class="d-inline-block align-middle mb-1">Let us know</span>
                  </span>
                </Button>
              </div>
            </div>
          </div>

          <div class="container">
            <div class="row mb-5">
              <div class="col-12">
                <Button :label="'Print / Download'" :onClick="printPage" :icon="'fa-cloud-download'" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="footer col-12 d-md-block d-sm-block d-lg-none d-xl-none">
          <div class="row parent-row h-100">
            <div class="col-12 share-button d-md-block d-sm-block d-lg-none d-xl-none">
              <div class="row h-100 align-items-center justify-content-center">
                <div class="col-auto pr-2">
                  <span class="tile__heading tile__heading--title m-0 p-0">Public observations</span>
                </div>

                <div class="col-auto">
                  <Button
                    :styles="{ 'button--public-obs--purple': true, 'text-truncate': true }"
                    :onClick="handleObservationButtonClick"
                  >
                    <span slot="label">
                      <PublicMarker :styles="{ 'd-inline-block': true, 'align-middle': true }" />
                      <span class="d-inline-block align-middle">Share now</span>
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Button from "../components/button/Button";
import DatesMixin from "../mixins/dates";
import Mapbox from "../components/map/Map";
import { mapState } from "vuex";
import PublicMarker from "../components/public-marker/PublicMarker";
import API from "../api/API";
import Loader from "../components/loader/Loader";
import VueEasyLightbox from "vue-easy-lightbox";

export default {
  components: {
    Loader,
    Button,
    Mapbox,
    PublicMarker,
    VueEasyLightbox
  },

  mixins: [DatesMixin],

  filters: {
    yesNo(value) {
      return value ? "Yes" : "No";
    },

    capitalise(value) {
      if (typeof value === "string") {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
    }
  },

  data() {
    return {
      prevRoute: null,
      isFetching: false,
      mobileMapVisible: false,
      index: 0,
      visible: false
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from;
    });
  },

  computed: {
    ...mapState({
      cms: state => state.cms,
      regions: state => state.regions.filter(region => region.geometry !== null),
      forecasts: state => state.forecasts,
      selectedObservation: state => state.selectedObservation
    }),

    observations() {
      return this.$store.getters.allObservationsGeoJson();
    },

    mapOptions() {
      const options = {
        zoom: 14
      };

      if (this.selectedObservation.id) {
        options.center = [this.selectedObservation.coordinates.lng, this.selectedObservation.coordinates.lat];
      }

      return options;
    },

    isAvalanche() {
      return this.selectedObservation.type === "Avalanche";
    },

    isSnowpack() {
      return this.selectedObservation.type === "Snowpack";
    },

    snowpackTestType() {
      return this.selectedObservation.footPenetration ? "Foot" : "Ski";
    },

    socialImage() {
      if (this.selectedObservation && this.selectedObservation.photos && this.selectedObservation.photos.length) {
        return this.selectedObservation.photos[0].full;
      }

      return this.cms.socialImage;
    },

    socialSharing() {
      let socialDescription = "";

      if (this.selectedObservation.comments && this.selectedObservation.comments.length > 0) {
        socialDescription = this.selectedObservation.comments;
      } else if (this.cms.PublicObservationDescription && this.cms.PublicObservationDescription.length > 0) {
        socialDescription = this.cms.PublicObservationDescription;
      } else {
        socialDescription = `I just shared a ${this.selectedObservation.type} observation from the New Zealand Mountain Safety Council.`;
      }

      return {
        url: window.location.href,
        title: `Public Observation: ${this.selectedObservation.type} - ${this.selectedObservation.description}`,
        description: socialDescription
      };
    },

    thumbnail() {
      return this.selectedObservation.photos.length ? this.selectedObservation.photos[0].thumb : null;
    },

    photos() {
      return this.selectedObservation.photos.length ? this.selectedObservation.photos.map(p => p.full) : [];
    }
  },

  methods: {
    handleMapLoad(mapbox) {
      mapbox.flyTo({
        center: [this.selectedObservation.coordinates.lng, this.selectedObservation.coordinates.lat],
        zoom: 18
      });
    },

    handleRegionSelect(region) {
      this.$store.dispatch("setSelectedRegion", region);
      this.$store.dispatch("setSelectedObservation", {});
      this.$router.push({ name: "region", params: { id: region.id } });
      this.$destroy();
    },

    handleObservationSelect(id) {
      this.$store.dispatch("setSelectedObservation", this.$store.getters.observationById(id));
      this.$router.push({ name: "observation", params: { id } });
    },

    handleReportObservationClick() {
      this.$router.push({ name: "observationform" });
    },

    handleMobileToggle(event, map) {
      this.mobileMapVisible = this.mobileMapVisible ? false : true;

      this.$nextTick(() => {
        if (typeof map !== "undefined") {
          map.resize();
        } else {
          window.dispatchEvent(new Event("resize"));
        }
      });
    },

    handleObservationButtonClick(event) {
      this.$router.push({ name: "observationform" });
    },

    goBack() {
      const prev = this.prevRoute;

      if (prev && ["region", "observations"].includes(prev.name)) {
        this.$router.back();
      } else {
        this.$router.push({ path: "/" });
      }

      this.$store.dispatch("setSelectedObservation", {});
      this.$destroy();
    },

    printPage() {
      window.print();
    },

    openLightbox(ev) {
      this.$refs.lightbox.show();
    },

    showImg(index) {
      this.index = index;
      this.visible = true;
    },
    handleHide() {
      this.visible = false;
    }
  },

  created() {
    // If there's no selected observation (the user has come directly to the page or has hard reloaded)
    // set the selected observation from the URL.
    if (!this.$store.state.selectedObservation.id) {
      const id = parseInt(this.$route.params.id, 10);
      let observation = this.$store.getters.observationById(id);

      // If the observation is not in the pre-loaded ones, try to get it from the server
      if (!observation) {
        this.isFetching = true;
        const data = API.fetch("observation", id).then(response => {
          if (response.data.observations.length) {
            this.$store.dispatch("setSelectedObservation", response.data.observations[0]);
            this.isFetching = false;
            this.$emit("updateHead");
          } else {
            // Redirect to homepage when the observation no longer exists
            window.location = "/";
          }
        });
      }
      this.$store.dispatch("setSelectedObservation", observation);
    }

    // Redirect to homepage when the observation no longer exists
    if (!this.selectedObservation && !this.isFetching) {
      window.location = "/";
    }
  },

  head: {
    meta() {
      const observation = this.selectedObservation;

      let socialDescription = "";

      if (observation && observation.description && observation.description > 0) {
        socialDescription = observation.description;
      } else if (this.cms.socialDescription && this.cms.socialDescription.length > 0) {
        socialDescription = this.cms.socialDescription;
      } else {
        socialDescription =
          "Planning to visit the outdoors? Check the Avalanche forecast from the New Zealand Mountain Safety Council.";
      }

      let tags = [
        { property: "og:title", content: "Public observation of snow conditions" },
        { property: "og:description", content: socialDescription },
        { property: "og:image", content: this.socialImage },
        { property: "og:image:width", content: 300 },
        { property: "og:image:height", content: 300 },
      ];

      if (observation && observation.coordinates) {
        tags.push(
          { property: "og:type", content: "place" },
          { property: "place:location:longitude", content: observation.coordinates.lng },
          { property: "place:location:latitude", content: observation.coordinates.lat }
        );
      }

      return tags;
    },

    title() {
      return {
        inner: "Public Observation"
      };
    }
  }
};
</script>
